import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Loader2, Copy, CheckCircle } from 'lucide-react';
import { useSchemes } from '../../contexts/SchemeContext';
import xbApi from '../../services/xbApi';
import ProfileStatusBadge from '../../components/xb/ProfileStatusBadge';
import { profileStatus, shortSchemeName } from '../../utils/crossBorder';
import { copyToClipboard } from '../../utils/helpers';

/** The page frame every Cross-border page uses: title, what the page is for, then the page. */
export function XbPage({ icon: Icon, title, intro, wide = false, children }) {
  return (
    <div className={`space-y-6 ${wide ? 'max-w-6xl' : 'max-w-4xl'}`}>
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          {Icon && <Icon className="w-6 h-6 text-primary-600" aria-hidden="true" />} {title}
        </h1>
        {intro && <p className="text-sm text-gray-500 mt-1">{intro}</p>}
      </div>
      {children}
    </div>
  );
}

/**
 * The scheme a page shows, or what to show instead: a spinner while the
 * schemes load, a note when there are none, the reason when the chosen one
 * cannot be used. `children` is called with the selected scheme.
 */
export function WithScheme({ children }) {
  const { schemes, selected } = useSchemes();
  if (schemes === null) {
    return <p className="flex items-center gap-2 text-sm text-gray-500 py-6">
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Reading your schemes…</p>;
  }
  if (!selected) {
    return <Notice tone="info" title="No cross-border scheme yet">
      Your institution takes part in no regional scheme in this Sandbox. An AfricaNenda operator assigns schemes
      in the Operator Console; ask your contact there.
    </Notice>;
  }
  if (selected.problem || !selected.profile) {
    return <Notice tone="danger" title="This scheme cannot be used as assigned">
      {selected.problem || 'This Sandbox no longer serves the profile.'}. An AfricaNenda operator corrects the
      assignment in the Operator Console.
    </Notice>;
  }
  return children(selected);
}

const NOTICE_TONE = {
  info:   'bg-primary-50 border-primary-200 text-primary-900',
  warn:   'bg-amber-50 border-amber-200 text-amber-900',
  danger: 'bg-red-50 border-red-200 text-red-800',
  ok:     'bg-green-50 border-green-200 text-green-900',
};

/** A boxed note: info, warn, danger or ok. */
export function Notice({ tone = 'info', title, icon, children }) {
  const Icon = icon || (tone === 'danger' ? AlertCircle : tone === 'warn' ? AlertTriangle : null);
  return (
    <div className={`border rounded-xl p-4 text-sm ${NOTICE_TONE[tone] || NOTICE_TONE.info}`}>
      {title && <p className="font-semibold flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />} {title}</p>}
      <div>{children}</div>
    </div>
  );
}

/** The scheme's name, status badge and version, as a page's subtitle. */
export function SchemeHeading({ summary }) {
  return (
    <div className="flex items-center gap-2 flex-wrap text-sm">
      <span className="font-semibold text-gray-900">{shortSchemeName(summary) || summary.id}</span>
      <ProfileStatusBadge status={summary.status} />
      <span className="font-mono text-xs text-gray-500">{summary.ref}</span>
    </div>
  );
}

/** What a provisional profile means for the reader, shown wherever its rules are relied on. */
export function ProvisionalNotice({ summary }) {
  if (summary?.status !== 'PROVISIONAL') return null;
  return (
    <Notice tone="warn" title="A provisional profile">
      {profileStatus('PROVISIONAL').note} Use it to rehearse your integration; passing here is not the scheme
      operator's approval.
    </Notice>
  );
}

/** A card with a heading. */
export function Card({ title, icon: Icon, actions, children, className = '' }) {
  return (
    <section className={`bg-white border border-gray-200 rounded-xl p-5 ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 mb-3">
          {title && <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-primary-600" aria-hidden="true" />} {title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** One fact: a label and its value, in a definition list. */
export function Fact({ label, children, mono = false }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <dt className="text-sm text-gray-500 shrink-0">{label}</dt>
      <dd className={`text-sm text-gray-900 text-right ${mono ? 'font-mono' : ''}`}>{children}</dd>
    </div>
  );
}

/** Inline code. */
export const C = ({ children }) => (
  <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-[0.85em]">{children}</code>
);

/** A value with a copy button, for paths and ids a participant will paste elsewhere. */
export function Copyable({ value, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (await copyToClipboard(value)) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };
  return (
    <span className="inline-flex items-center gap-1.5 max-w-full">
      <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-mono break-all">{value}</code>
      <button type="button" onClick={copy} aria-label={`Copy ${label || value}`}
        className="p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 shrink-0">
        {copied ? <CheckCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
                : <Copy className="w-4 h-4" aria-hidden="true" />}
      </button>
    </span>
  );
}

/** A link styled as a secondary button. */
export const LinkButton = ({ to, children }) => (
  <Link to={to} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white
    text-sm font-medium text-gray-700 hover:bg-gray-50">{children}</Link>
);

// Full profiles are read once per profile and kept for the session's pages.
const detailCache = new Map();

/**
 * The selected scheme's profile in full ({ summary, assignment, profile,
 * incoming, endpoint }), or an error. Kept per profile id and version.
 */
export function useSchemeDetail(scheme) {
  const key = scheme?.profile?.ref;
  const [state, setState] = useState(() => ({ detail: key ? detailCache.get(key) || null : null, error: null }));
  useEffect(() => {
    if (!scheme || !key) return undefined;
    if (detailCache.has(key)) { setState({ detail: detailCache.get(key), error: null }); return undefined; }
    let live = true;
    setState({ detail: null, error: null });
    xbApi.profile(scheme.profileId)
      .then((d) => { detailCache.set(key, d); if (live) setState({ detail: d, error: null }); })
      .catch((e) => { if (live) setState({ detail: null, error: errorText(e) }); });
    return () => { live = false; };
  }, [scheme, key]);
  return state;
}

/** The engine's message for a failed call, else the transport's. */
export function errorText(e) {
  return e?.response?.data?.message || e?.response?.data?.error || e?.message || 'The request failed.';
}

/** Amounts as the engine gives them, with the currency. */
export const money = (a) => (a ? `${a.amount} ${a.currency}` : '—');

/** Monospaced block with a copy button. */
export function CodeBlock({ children, copy = true, className = '' }) {
  const [copied, setCopied] = useState(false);
  const text = String(children ?? '');
  const onCopy = async () => {
    if (await copyToClipboard(text)) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };
  return (
    <div className={`relative ${className}`}>
      <pre className="bg-gray-900 text-gray-100 text-xs font-mono rounded-lg p-3 pr-16 overflow-x-auto whitespace-pre max-h-96">{text}</pre>
      {copy && text && (
        <button onClick={onCopy} type="button"
          className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-gray-700 text-gray-200 hover:bg-gray-600">
          {copied ? <CheckCircle className="w-3 h-3" aria-hidden="true" /> : <Copy className="w-3 h-3" aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      )}
    </div>
  );
}

/** A table: head is a list of column names, rows a list of cell lists. */
export function Table({ head, rows, mono = [], empty = 'None.' }) {
  if (!rows?.length) return <p className="text-sm text-gray-500">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
            {head.map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-gray-100 last:border-0 align-top">
              {r.map((cell, j) => (
                <td key={j} className={`py-2 pr-4 text-gray-800 ${mono.includes(j) ? 'font-mono text-xs' : ''}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
